import React, { useState, useEffect, useRef } from "react";
import { Select, Input, Button, message } from "antd";
import { LeftOutlined, SearchOutlined } from "@ant-design/icons";
import './style/index.less'
import { getCertificateTypeList, getCorruptionList } from "./api";
import { isIdCard } from '@qnvip/core';

const { Option } = Select;

interface CertificateType {
    code: string;
    name: string;
}

const protectImg = require('../../assets/images/integrity/protect.png');
const harmImg = require('../../assets/images/integrity/harm.png');

const rules = [
    /^[EG]\d{8}$/,
    /^K\d{8}$/,
    /^M\d{8}$/,
    /^[A-Z]\d{8}$/,
    /^[A-Z]\d{7,8}$/,
    /^[A-Z]{2}\d{7}$/,
    /^[A-Z]{2}\d{6}$/,
    /^[A-Z]\d{7,8}$/,
    /^[A-Z]{2}\d{7}$/,
    /^[A-Z]{2}\d{7}$/,
    /^[A-Z]\d{8}$/,
    /^[A-Z]{2}\d{7,8}$/,
    /^[A-Z]\d{7,8}$/,
    /^[A-Z0-9]{6,12}$/
]

/**
 * 贪腐解聘查询组件
 * 用于查询人员的贪腐解聘情况
 */
const Integrity = () => {
    const [certificateTypes, setCertificateTypes] = useState<CertificateType[]>([]); // 证件类型列表
    const [certificateType, setCertificateType] = useState<string>(""); // 当前选中的证件类型
    const [certificateCode, setCertificateCode] = useState<string>(""); // 证件号码
    const [userName, setUserName] = useState<string>(""); // 要查询的人员姓名
    const [loading, setLoading] = useState<boolean>(false); // 加载状态
    const [resultFlag, setResultFlag] = useState<boolean>(false); // 查询结果标志
    const [showResult, setShowResult] = useState<boolean>(false); // 是否显示查询结果
    const [checkState, setCheckState] = useState<boolean>(true); // 号码验证是否通过

    const checkStateRef = useRef(checkState);

    // 获取证件类型列表
    useEffect(() => {
        fetchCertificateTypes();
    }, []);

    useEffect(() => {
        checkStateRef.current = checkState;
    }, [checkState])

    const fetchCertificateTypes = async () => {
        const res = await getCertificateTypeList();
        const resData = res.data.map((item: string, index: number) => ({
            code: index.toString(),
            name: item
        }))

        setCertificateTypes(resData);
        // 默认选中第一个
        if (resData.length > 0) {
            setCertificateType(resData[0].code);
        }
    };

    // 查询按钮点击
    const handleSearch = async () => {
        if (!userName.trim()) {
            message.warning("请输入要查询的人员姓名");
            return;
        }
        if (!certificateCode.trim()) {
            message.warning("请输入证件号码");
            return;
        }

        inputBlur(certificateCode)

        if (!checkStateRef.current) {
            return;
        }

        setLoading(true);
        const selectedType = certificateTypes.find(t => t.code === certificateType);
        const res = await getCorruptionList({
            certificateType: selectedType?.name || certificateType,
            certificateCode: certificateCode.trim(),
            userName: userName.trim()
        });

        // 处理查询结果
        if (!res.data) {
            setResultFlag(false)
        } else {
            setResultFlag(true)
        }
        setShowResult(true)
        setLoading(false);
    };

    // 返回查询界面
    const handleReturn = () => {
        setCertificateType('0')
        setUserName('')
        setCertificateCode('')
        setShowResult(false);
    }

    // 证件号码输入框变化
    const numberChnage = function (e: any) {
        const value = e.target.value.trim();
        setCertificateCode(value)
    }

    // 输入框失去焦点时 
    const inputBlur = function (value: string) {
        let numberFlag = false;

        if (certificateType === '0') { // 验证大陆身份证
            numberFlag = isIdCard(value)
        } else if (certificateType === '1') { // 验证护照
            for (let i = 0; i < rules.length; i++) {
                const rule = rules[i];
                numberFlag = rule.test(value)
                if (numberFlag) {
                    break;
                }
            }
        } else if (certificateType === '2') {
            if (/^[A-Z]{1,2}[0-9]{6}\([A0-9]\)$/.test(value)) {
                numberFlag = true;
                return
            }
            if (/^[157][0-9]{6}\([0-9A]\)$/.test(value)) {
                numberFlag = true;
            }
        }
        setCheckState(numberFlag)
    }

    const getCheckStateMsg = () => {
        if (certificateType === '0') { // 验证大陆身份证
            return '请输入正确的身份证号码'
        } else if (certificateType === '1') { // 验证护照
            return '请输入正确的护照号码'
        } else if (certificateType === '2') {
            return '请输入正确的港澳身份证号码'
        }
    }

    return (
        <div className="integrity-page-wrapper">
            <div className="integrity-query-card">
                {/* 标题 */}
                <h1 className="integrity-title">贪腐解聘查询</h1>

                {/* 返回按钮 */}
                {showResult && <div className="return-btn" onClick={handleReturn}>
                    <LeftOutlined />
                    <span>返回</span>
                </div>}
                {/* 表单区域 */}
                {!showResult && <div className="integrity-form">
                    {/* 证件类型 */}
                    <div className="form-item">
                        <div className="form-label">证件类型</div>
                        <Select
                            className="form-select"
                            value={certificateType}
                            onChange={setCertificateType}
                            popupClassName="integrity-form-select"
                        >
                            {certificateTypes.map((type) => (
                                <Option key={type.code} value={type.code}>
                                    {type.name}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* 人员姓名 */}
                    <div className="form-item">
                        <div className="form-label">
                            要查询的人员姓名<span className="required">*</span>
                        </div>
                        <Input
                            className="form-input"
                            placeholder="请输入姓名"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                        />
                    </div>

                    {/* 证件号码 */}
                    <div className={`form-item ${checkState ? '' : 'error'}`} data-msg={getCheckStateMsg()}>
                        <div className="form-label">
                            证件号码<span className="required">*</span>
                        </div>
                        <Input
                            className="form-input"
                            placeholder="请输入证件号码"
                            value={certificateCode}
                            onChange={numberChnage}
                            onBlur={(e) => inputBlur(e.target.value)}
                        />
                    </div>

                    {/* 查询按钮 */}
                    <Button
                        className="search-btn"
                        type="primary"
                        loading={loading}
                        onClick={handleSearch}
                        icon={<SearchOutlined />}
                    >
                        查询
                    </Button>
                </div>}
                {/* 查询结果 */}
                {showResult && <div className="integrity-result">
                    <div className="result-info">
                        <img src={resultFlag ? harmImg : protectImg} alt="" />
                    </div>
                    <p className="result-content">{resultFlag ? '存在贪腐解聘记录' : ' 无相关记录'}</p>
                </div>}
                {/* 底部链接 */}
                <div className="integrity-footer">
                    <div className="guide-link">
                        <p>如需了解人员贪腐解聘处理情况详情，请致电或专门发函咨询:</p>
                        <div>
                            <span> 联系电话：17380117610 </span>
                            <span> 邮箱：audit@jzfz.com.cn </span>
                        </div>
                    </div>
                    <p className="footer-tips">
                        查询结果仅限合法用途，不得泄露或用于不当目的
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Integrity;
